// Ein-/Ausschaltzeiten (Erweiterung): Übersicht und Wochenraster-Editor (Seite Zeitpläne), Register „Ein/Aus" im
// Bildschirm-Detail mit Zustand, gemeldetem Anzeigezustand und „Jetzt einschalten/ausschalten".
import '../styles/emergency-power.css';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import {
  qkEp, useDevicePower, usePowerSchedules, WEEKDAYS, type PowerException, type PowerSchedule, type PowerTargetType, type PowerWindow,
} from '../api/emergency-power';
import { can, useDevices, useGroups, useLocations, useMe } from '../api/queries';
import type { DeviceDetail } from '../api/types';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Confirm, Dialog, Field, formatDateTime, relativeTime } from '../components/ui';
import { localeOf } from '../i18n';
import { daySegments, fromGrid, hasMultiple, toGrid, weeklyHours, type WeekGrid } from './power-lib';

const ORDER: PowerTargetType[] = ['tenant', 'location', 'group', 'device'];

function useTargetName() {
  const { t } = useTranslation();
  const { data: devices = [] } = useDevices();
  const { data: groups = [] } = useGroups();
  const { data: locations = [] } = useLocations();
  return (type: PowerTargetType, id: string | null) => type === 'tenant' ? t('assign.allScreens')
    : (type === 'location' ? locations : type === 'group' ? groups : devices).find((x) => x.id === id)?.name ?? '–';
}

/** Wochenansicht: je Tag ein 24-h-Balken mit den eingeschalteten Abschnitten. */
export function WeekBars({ weekly, disabled }: { weekly: readonly PowerWindow[]; disabled?: boolean }) {
  const { t } = useTranslation();
  const days = t('schedules.weekdaysShort', { returnObjects: true }) as string[];
  return (
    <div className="col" style={{ gap: 4 }}>
      <div className="pw-week">
        {WEEKDAYS.map((d, i) => (
          <WeekRow key={d} label={days[i] ?? d} segs={disabled ? [[0, 1440]] : daySegments(weekly, d)} disabled={!!disabled} />
        ))}
        <span />
        <div className="week-axis"><span>0</span><span>6</span><span>12</span><span>18</span><span>24</span></div>
      </div>
    </div>
  );
}

function WeekRow({ label, segs, disabled }: { label: string; segs: [number, number][]; disabled: boolean }) {
  return (
    <>
      <span className="pw-day">{label}</span>
      <Bar segs={segs} disabled={disabled} />
    </>
  );
}

function Bar({ segs, disabled }: { segs: [number, number][]; disabled?: boolean }) {
  const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  return (
    <div className={`week-bar${disabled ? ' disabled' : ''}`} data-testid="week-bar">
      {[6, 12, 18].map((h) => <span key={h} className="tick" style={{ left: `${(h / 24) * 100}%` }} />)}
      {segs.map(([a, b]) => (
        <span key={a} className="seg" title={`${hhmm(a)}–${hhmm(b)}`} style={{ left: `${(a / 1440) * 100}%`, width: `${((b - a) / 1440) * 100}%` }} />
      ))}
    </div>
  );
}

/** Seite Zeitpläne: alle Ein-/Ausschaltzeiten des Mandanten. */
export function PowerSchedulesCard() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: list = [] } = usePowerSchedules();
  const name = useTargetName();
  const qc = useQueryClient();
  const toast = useToast();
  const [edit, setEdit] = useState<{ targetType: PowerTargetType; targetId: string | null; current: PowerSchedule | null } | null>(null);
  const [del, setDel] = useState<PowerSchedule | null>(null);
  const manage = can(me, 'devices.manage');
  const sorted = [...list].sort((a, b) => ORDER.indexOf(a.targetType) - ORDER.indexOf(b.targetType));
  const remove = async () => {
    if (!del) return;
    try {
      await api.del(`/power-schedules/${del.id}`);
      await qc.invalidateQueries({ queryKey: qkEp.powerSchedules });
      setDel(null);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <div className="card" style={{ marginTop: 18 }} data-testid="power-card">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 className="grow" style={{ margin: 0 }}>{t('power.title')}</h2>
        {manage && <button className="btn sm" onClick={() => setEdit({ targetType: 'tenant', targetId: null, current: list.find((x) => x.targetType === 'tenant') ?? null })}>
          <Icon name="clock" />{t('power.add')}</button>}
      </div>
      <div className="muted small" style={{ marginBottom: 10 }}>{t('power.sub')} {t('power.precedence')}</div>
      {sorted.length ? (
        <div className="col" style={{ gap: 14 }}>
          {sorted.map((s) => (
            <div key={s.id} className="col" style={{ gap: 6 }}>
              <div className="row">
                <span className="pill neutral plain">{t(`assign.via.${s.targetType}`)}</span>
                <b className="grow">{name(s.targetType, s.targetId)}</b>
                {!s.enabled && <span className="pill ok plain">{t('power.alwaysOn')}</span>}
                {s.enabled && <span className="muted small">{t('power.hours', { hours: Math.round(weeklyHours(s.weekly)) })}{s.exceptions.length ? ` · ${t('power.exceptionCount', { count: s.exceptions.length })}` : ''}</span>}
                {manage && <button className="btn sm ghost" onClick={() => setEdit({ targetType: s.targetType, targetId: s.targetId, current: s })}>{t('common.change')}</button>}
                {manage && <button className="btn sm ghost icon" aria-label={t('common.remove')} onClick={() => setDel(s)}><Icon name="trash" /></button>}
              </div>
              <WeekBars weekly={s.weekly} disabled={!s.enabled} />
            </div>
          ))}
        </div>
      ) : <div className="muted">{t('power.none')}</div>}
      {edit && <PowerEditorDialog {...edit} onClose={() => setEdit(null)} />}
      {del && <Confirm title={t('power.deleteConfirm', { name: name(del.targetType, del.targetId) })} danger confirmLabel={t('common.remove')}
        onConfirm={() => void remove()} onClose={() => setDel(null)} />}
    </div>
  );
}

const DEFAULT_GRID: WeekGrid = toGrid([
  { weekdays: ['mon', 'tue', 'wed', 'thu', 'fri'], on: '06:00', off: '22:00' },
  { weekdays: ['sat', 'sun'], on: '08:00', off: '20:00' },
]);

/** Editor: Ziel, Wochenraster (ein Fenster je Tag), Ausnahmen. */
export function PowerEditorDialog({ targetType, targetId, current, fixedTarget, onClose }: {
  targetType: PowerTargetType; targetId: string | null; current: PowerSchedule | null; fixedTarget?: boolean; onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: list = [] } = usePowerSchedules();
  const { data: devices = [] } = useDevices();
  const { data: groups = [] } = useGroups();
  const { data: locations = [] } = useLocations();
  const name = useTargetName();
  const days = t('schedules.weekdaysShort', { returnObjects: true }) as string[];
  const [type, setType] = useState<PowerTargetType>(targetType);
  const [target, setTarget] = useState<string | null>(targetId);
  const [base, setBase] = useState<PowerSchedule | null>(current);
  const [enabled, setEnabled] = useState(current?.enabled ?? true);
  const [grid, setGrid] = useState<WeekGrid>(current ? toGrid(current.weekly) : DEFAULT_GRID);
  const [exceptions, setExceptions] = useState<PowerException[]>(current?.exceptions ?? []);
  const [busy, setBusy] = useState(false);

  /** Zielwechsel: vorhandenen Zeitplan des Ziels laden. */
  const pickTarget = (tt: PowerTargetType, id: string | null) => {
    setType(tt);
    setTarget(id);
    const ex = list.find((x) => x.targetType === tt && x.targetId === id) ?? null;
    setBase(ex);
    setEnabled(ex?.enabled ?? true);
    setGrid(ex ? toGrid(ex.weekly) : DEFAULT_GRID);
    setExceptions(ex?.exceptions ?? []);
  };
  const options = type === 'location' ? locations : type === 'group' ? groups : type === 'device' ? devices.filter((d) => d.status === 'active') : [];
  const weekly = fromGrid(grid);
  const setDay = (d: keyof WeekGrid, v: WeekGrid[keyof WeekGrid]) => setGrid({ ...grid, [d]: v });

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/power-schedules', {
        targetType: type, targetId: type === 'tenant' ? null : target, enabled, weekly,
        exceptions: exceptions.filter((x) => x.date).map((x) => ({ ...x, label: x.label?.trim() || null })),
      });
      await Promise.all([qc.invalidateQueries({ queryKey: qkEp.powerSchedules }), qc.invalidateQueries({ queryKey: ['devices'] })]);
      toast.show(t('power.saved'));
      onClose();
    } catch (e) {
      toast.error(e);
      setBusy(false);
    }
  };

  return (
    <Dialog wide title={fixedTarget ? t('power.editTitle', { name: name(type, target) }) : t('power.title')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={busy || (type !== 'tenant' && !target)} onClick={() => void save()}>{t('common.save')}</button>
    </>}>
      {!fixedTarget && (
        <div className="row" style={{ gap: 12 }}>
          <Field label={t('power.target')}>
            <select value={type} onChange={(e) => pickTarget(e.target.value as PowerTargetType, null)}>
              {ORDER.map((x) => <option key={x} value={x}>{x === 'tenant' ? t('assign.allScreens') : t(`assign.via.${x}`)}</option>)}
            </select>
          </Field>
          {type !== 'tenant' && (
            <Field label={t(`assign.via.${type}`)}>
              <select value={target ?? ''} onChange={(e) => pickTarget(type, e.target.value || null)}>
                <option value="">{t('common.choose')}</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </Field>
          )}
        </div>
      )}
      <label className="check"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />{t('power.enabled')}</label>
      {!enabled ? <div className="hint">{t('power.disabledHint')}</div> : (
        <>
          {base && hasMultiple(base.weekly) && <div className="hint warn">{t('power.multipleHint')}</div>}
          <div className="week-edit" role="group" aria-label={t('power.week')}>
            {WEEKDAYS.map((d, i) => {
              const w = grid[d];
              return (
                <div key={d} style={{ display: 'contents' }} data-testid={`day-${d}`}>
                  <label className="check"><input type="checkbox" checked={!!w} aria-label={`${days[i]} ${t('power.dayOn')}`}
                    onChange={(e) => setDay(d, e.target.checked ? (grid.mon ?? { on: '06:00', off: '22:00' }) : null)} /><b>{days[i]}</b></label>
                  <input type="time" aria-label={`${days[i]} ${t('power.from')}`} disabled={!w} value={w?.on ?? ''} onChange={(e) => w && e.target.value && setDay(d, { ...w, on: e.target.value })} />
                  <input type="time" aria-label={`${days[i]} ${t('power.until')}`} disabled={!w} value={w?.off === '24:00' ? '00:00' : (w?.off ?? '')} onChange={(e) => w && e.target.value && setDay(d, { ...w, off: e.target.value })} />
                  <Bar segs={daySegments(weekly, d)} />
                </div>
              );
            })}
          </div>
          <div className="row" style={{ gap: 10 }}>
            <button type="button" className="btn sm" disabled={!grid.mon} onClick={() => setGrid(Object.fromEntries(WEEKDAYS.map((d) => [d, grid.mon])) as WeekGrid)}>{t('power.copyMon')}</button>
            <span className="muted small grow">{t('power.hours', { hours: Math.round(weeklyHours(weekly)) })} · {t('power.overnightHint')}</span>
          </div>
          <h3 style={{ margin: '8px 0 0' }}>{t('power.exceptions')}</h3>
          {exceptions.length ? exceptions.map((x, i) => {
            const upd = (p: Partial<PowerException>) => setExceptions(exceptions.map((y, j) => (j === i ? { ...y, ...p } : y)));
            const kind = x.mode === 'off' ? 'off' : x.on ? 'times' : 'on';
            return (
              <div key={i} className="exc-row" data-testid="exception">
                <input type="date" aria-label={t('power.excDate')} value={x.date} onChange={(e) => upd({ date: e.target.value })} />
                <input type="date" aria-label={t('power.excUntil')} value={x.dateUntil ?? ''} onChange={(e) => upd({ dateUntil: e.target.value || null })} />
                <select aria-label={t('power.excMode')} value={kind} onChange={(e) => upd(e.target.value === 'off' ? { mode: 'off', on: null, off: null }
                  : e.target.value === 'on' ? { mode: 'on', on: null, off: null } : { mode: 'on', on: '10:00', off: '16:00' })}>
                  <option value="off">{t('power.excOff')}</option><option value="on">{t('power.excOn')}</option><option value="times">{t('power.excTimes')}</option>
                </select>
                {kind === 'times' ? (
                  <span className="row" style={{ gap: 6 }}>
                    <input type="time" aria-label={t('power.from')} value={x.on ?? ''} onChange={(e) => upd({ on: e.target.value })} />
                    <input type="time" aria-label={t('power.until')} value={x.off ?? ''} onChange={(e) => upd({ off: e.target.value })} />
                  </span>
                ) : <input aria-label={t('power.excLabel')} placeholder={t('power.excLabelPh')} value={x.label ?? ''} onChange={(e) => upd({ label: e.target.value })} />}
                <button type="button" className="btn sm ghost icon" aria-label={t('common.remove')} onClick={() => setExceptions(exceptions.filter((_, j) => j !== i))}><Icon name="trash" /></button>
              </div>
            );
          }) : <div className="muted small">{t('power.noExceptions')}</div>}
          <div><button type="button" className="btn sm" onClick={() => setExceptions([...exceptions, { date: '', mode: 'off', label: '' }])}><Icon name="plus" />{t('power.addException')}</button></div>
          <div className="hint">{t('power.blackHint')}</div>
        </>
      )}
    </Dialog>
  );
}

const OVERRIDE_HOURS = [1, 2, 4, 8] as const;

/** Register „Ein/Aus" im Bildschirm-Detail. */
export function ScreenPowerTab({ device }: { device: DeviceDetail }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: p, refetch } = useDevicePower(device.id);
  const { data: groups = [] } = useGroups();
  const { data: locations = [] } = useLocations();
  const toast = useToast();
  const [howLong, setHowLong] = useState<'next' | number>('next');
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!p) return <div className="card muted">{t('common.loading')}</div>;
  const command = can(me, 'devices.command') && device.status === 'active';
  const manage = can(me, 'devices.manage') && device.status === 'active';

  const override = async (mode: 'on' | 'off') => {
    setBusy(true);
    try {
      await api.post('/power-overrides', { targetType: 'device', targetId: device.id, mode, ...(howLong === 'next' ? { untilNextChange: true } : { durationMinutes: howLong * 60 }) });
      toast.show(t(mode === 'on' ? 'power.sentOn' : 'power.sentOff'));
      await refetch();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => {
    setBusy(true);
    try {
      await api.post('/power-overrides/clear', { targetType: 'device', targetId: device.id });
      toast.show(t('power.cleared'));
      await refetch();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const viaName = p.via === 'device' ? t('power.viaDevice') : p.via === 'tenant' ? t('assign.allScreens')
    : p.via === 'group' ? `${t('assign.via.group')} ${groups.find((g) => g.id === p.schedule?.targetId)?.name ?? ''}`
    : p.via === 'location' ? `${t('assign.via.location')} ${locations.find((l) => l.id === p.schedule?.targetId)?.name ?? ''}` : '';
  const source = p.state.source === 'override' && p.override ? t('power.src.override', { until: formatDateTime(p.override.until, locale) }) : t(`power.src.${p.state.source}`);

  return (
    <div className="grid-2">
      <div className="card col" data-testid="power-state">
        <div className="power-now">
          <span className={`power-dot ${p.state.on ? 'on' : 'off'}`}><Icon name={p.state.on ? 'sun' : 'moon'} /></span>
          <div className="col" style={{ gap: 2 }}>
            <b style={{ fontSize: 20 }}>{p.state.on ? t('power.stateOn') : t('power.stateOff')}</b>
            <span className="muted small">{source}</span>
          </div>
        </div>
        <div className="muted small">{p.state.nextChangeAt ? t('power.next', { when: formatDateTime(p.state.nextChangeAt, locale) }) : t('power.noNext')}</div>
        <div className="small">{p.reported ? t('power.reported', { state: p.reported.display === 'on' ? t('power.stateOn') : t('power.stateOff'), when: relativeTime(p.reported.at, locale) }) : <span className="muted">{t('power.notReported')}</span>}</div>
        {command && (
          <>
            <Field label={t('power.forHow')}>
              <select value={String(howLong)} onChange={(e) => setHowLong(e.target.value === 'next' ? 'next' : Number(e.target.value))}>
                <option value="next">{t('power.untilNext')}</option>
                {OVERRIDE_HOURS.map((h) => <option key={h} value={h}>{t('power.forHours', { count: h })}</option>)}
              </select>
            </Field>
            <div className="row">
              <button className="btn primary" disabled={busy} onClick={() => void override('on')}><Icon name="sun" />{t('power.turnOn')}</button>
              <button className="btn" disabled={busy} onClick={() => void override('off')}><Icon name="moon" />{t('power.turnOff')}</button>
              {p.override && <button className="btn ghost" disabled={busy} onClick={() => void clear()}><Icon name="refresh" />{t('power.backToSchedule')}</button>}
            </div>
          </>
        )}
      </div>
      <div className="card col">
        <h2 style={{ margin: 0 }}>{t('power.title')}</h2>
        {p.schedule ? (
          <>
            <div className="muted small">{t('power.scheduleFrom', { via: viaName })} · {t('power.timezone', { tz: p.timezone })}</div>
            <WeekBars weekly={p.schedule.weekly} disabled={!p.schedule.enabled} />
            {p.schedule.exceptions.length > 0 && <div className="muted small">{t('power.exceptionCount', { count: p.schedule.exceptions.length })}</div>}
          </>
        ) : <div className="muted">{t('power.noSchedule')}</div>}
        {manage && <div><button className="btn sm" onClick={() => setEdit(true)}><Icon name="clock" />{t('power.ownSchedule')}</button></div>}
      </div>
      {edit && <PowerEditorDialog fixedTarget targetType="device" targetId={device.id} current={p.via === 'device' ? p.schedule : null}
        onClose={() => { setEdit(false); void refetch(); }} />}
    </div>
  );
}
